import React, { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../i18n/TModules';
import { OrbitBodyText, orbitTokens, orbitType } from '../styles';

const SCREEN_NAME = 'orbit';

const Fallback = styled.div`
  > * + * {
    margin-top: 8px;
  }
`;

const RetryButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  color: ${orbitTokens.action};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 500;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    outline: none;
  }
`;

interface IOrbitBodyFallbackProps {
  onRetry: () => void;
}

const OrbitBodyFallback: React.FC<IOrbitBodyFallbackProps> = ({ onRetry }) => {
  const { t } = useMultiLanguageTranslation();

  return (
    <Fallback role="alert" data-testid="orbit-body-error">
      <OrbitBodyText>{t(`${TOrbit}.notice.renderFailed`)}</OrbitBodyText>
      <RetryButton type="button" data-testid="orbit-body-error-retry" onClick={onRetry}>
        {t(`${TOrbit}.actions.retry`)}
      </RetryButton>
    </Fallback>
  );
};

export interface IOrbitBodyErrorBoundaryProps {
  children?: React.ReactNode;
  /**
   * Replaces the retry card. `OrbitShell` passes the bare page content here so that a throw in the
   * provider or the panel chrome degrades to "the dashboard without Orbit" rather than to the retry
   * card standing where the whole routed app used to be.
   */
  fallback?: React.ReactNode;
}

/**
 * Keeps a render failure inside the panel (FR §4.20) instead of letting it reach the app-level
 * boundary, which would replace the whole dashboard with the full-page fallback.
 */
const OrbitBodyErrorBoundary: React.FC<IOrbitBodyErrorBoundaryProps> = ({ children, fallback }) => {
  const handleBeforeCapture = useCallback((scope: Sentry.Scope) => {
    scope.setContext('screen', { screenName: SCREEN_NAME });
    scope.setTag('screen.name', SCREEN_NAME);
  }, []);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={handleBeforeCapture}
      fallback={({ resetError }) =>
        fallback === undefined ? <OrbitBodyFallback onRetry={resetError} /> : <>{fallback}</>
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default OrbitBodyErrorBoundary;
